import React from 'react'
import { Row, Col } from 'react-bootstrap'

// Base skeleton animation styles (inline for portability)
const skeletonKeyframes = `
@keyframes skeleton-pulse {
  0% { opacity: 1; }
  50% { opacity: 0.4; }
  100% { opacity: 1; }
}
@keyframes skeleton-shimmer {
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
}
`

const baseStyle: React.CSSProperties = {
  backgroundColor: '#e2e8f0',
  borderRadius: '4px',
  animation: 'skeleton-pulse 1.5s ease-in-out infinite',
}

// Inject keyframes into document head (once)
if (typeof document !== 'undefined') {
  const styleId = 'skeleton-keyframes'
  if (!document.getElementById(styleId)) {
    const style = document.createElement('style')
    style.id = styleId
    style.textContent = skeletonKeyframes
    document.head.appendChild(style)
  }
}

interface SkeletonProps {
  width?: string | number
  height?: string | number
  className?: string
  style?: React.CSSProperties
}

// Basic skeleton box
export const Skeleton: React.FC<SkeletonProps> = ({
  width = '100%',
  height = '20px',
  className = '',
  style = {}
}) => (
  <div
    className={`skeleton ${className}`}
    style={{
      ...baseStyle,
      width,
      height,
      ...style
    }}
  />
)

// Skeleton for a chart area
export const ChartSkeleton: React.FC<{ height?: number }> = ({ height = 300 }) => (
  <div className="chart-skeleton" style={{
    backgroundColor: '#f8fafc',
    borderRadius: '4px',
    border: '1px solid #e2e8f0',
    margin: '5px',
    padding: '16px',
    minHeight: height + 60,
  }}>
    {/* Chart title */}
    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '16px' }}>
      <Skeleton width="150px" height="20px" />
      <Skeleton width="80px" height="20px" />
    </div>
    {/* Chart area */}
    <Skeleton width="100%" height={`${height}px`} style={{ borderRadius: '4px' }} />
  </div>
)

// Skeleton for a stats/metrics card - matches MetricBox height
// Actual MetricBox: padding 5px 10px, p (16px), h3 (margin-top 10px, ~32px value)
export const StatCardSkeleton: React.FC = () => (
  <div className="stat-card-skeleton" style={{
    padding: '10px 15px',
    minHeight: '100px', // Match actual MetricBox total height (including title + value)
  }}>
    {/* Title - matches p tag 16px */}
    <Skeleton width="55%" height="16px" style={{ marginBottom: '1rem' }} />
    {/* Value - matches h3 ~32px */}
    <Skeleton width="50%" height="32px" />
  </div>
)

// Skeleton for multiple stat cards - matches MetricBoxes layout
export const StatsRowSkeleton: React.FC<{ count?: number; columnsPerRow?: number }> = ({
  count = 6,
  columnsPerRow = 3
}) => {
  return (
    <div className="stats-row-skeleton" style={{
      display: 'flex',
      flexWrap: 'wrap',
      gap: '10px',
      marginBottom: '10px',
    }}>
      {Array.from({ length: count }).map((_, i) => (
        <div
          key={i}
          style={{
            flex: `1 1 calc(${100 / columnsPerRow}% - 10px)`,
            minWidth: '200px',
            backgroundColor: '#f8fafc',
            borderRadius: '4px',
            border: '1px solid #e2e8f0',
          }}
        >
          <StatCardSkeleton />
        </div>
      ))}
    </div>
  )
}

// Skeleton for a table
export const TableSkeleton: React.FC<{ rows?: number, columns?: number }> = ({
  rows = 5,
  columns = 4
}) => (
  <div className="table-skeleton">
    {/* Header */}
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: `repeat(${columns}, 1fr)`,
        gap: '16px',
        padding: '12px 16px',
        borderBottom: '2px solid #e2e8f0',
        marginBottom: '8px'
      }}
    >
      {Array.from({ length: columns }).map((_, i) => (
        <Skeleton key={i} width="80%" height="16px" />
      ))}
    </div>
    {/* Rows */}
    {Array.from({ length: rows }).map((_, rowIndex) => (
      <div
        key={rowIndex}
        style={{
          display: 'grid',
          gridTemplateColumns: `repeat(${columns}, 1fr)`,
          gap: '16px',
          padding: '12px 16px',
          borderBottom: '1px solid #f1f5f9'
        }}
      >
        {Array.from({ length: columns }).map((_, colIndex) => (
          <Skeleton
            key={colIndex}
            width={colIndex === 0 ? '90%' : '60%'}
            height="14px"
          />
        ))}
      </div>
    ))}
  </div>
)

// Skeleton for filter sidebar
export const FilterSkeleton: React.FC<{ sections?: number }> = ({ sections = 4 }) => (
  <div className="filter-skeleton" style={{ padding: '16px' }}>
    {Array.from({ length: sections }).map((_, i) => (
      <div key={i} style={{ marginBottom: '24px' }}>
        <Skeleton width="50%" height="16px" style={{ marginBottom: '12px' }} />
        <Skeleton width="100%" height="36px" style={{ marginBottom: '8px' }} />
        <Skeleton width="100%" height="36px" />
      </div>
    ))}
  </div>
)

// Skeleton for a single collapsed filter item (matching FilterItem accordion style)
export const FilterItemSkeleton: React.FC = () => (
  <div className="filter-item-skeleton" style={{ marginBottom: '4px' }}>
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '8px 12px',
        backgroundColor: '#f8fafc',
        borderRadius: '4px',
      }}
    >
      <Skeleton width="60%" height="14px" />
      <Skeleton width="16px" height="16px" />
    </div>
  </div>
)

// Dashboard Overview skeleton
interface DashboardOverviewSkeletonProps {
  metricCount?: number
  chartCount?: number
  trendsHeight?: number
}

export const DashboardOverviewSkeleton: React.FC<DashboardOverviewSkeletonProps> = ({
  metricCount = 6,
  chartCount = 2,
  trendsHeight = 300
}) => {
  return (
    <div className="dashboard-overview-skeleton">
      {/* Metric boxes */}
      <StatsRowSkeleton count={metricCount} columnsPerRow={3} />

      {/* Divider - matches Overview Row/Col structure */}
      <Row className="chart-grid"><Col xs={12} style={{padding: '0 5px'}}><hr style={{padding: '0'}} className="col-xs-12"/></Col></Row>

      {/* Chart controls row */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '16px',
        padding: '0 5px',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Skeleton width="100px" height="36px" style={{ borderRadius: '4px' }} />
          <span style={{ color: '#94a3b8', fontSize: '14px' }}>vs</span>
          <Skeleton width="100px" height="36px" style={{ borderRadius: '4px' }} />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ color: '#94a3b8', fontSize: '14px' }}>By</span>
          <Skeleton width="80px" height="36px" style={{ borderRadius: '4px' }} />
        </div>
      </div>

      {/* Trends chart */}
      <ChartSkeleton height={trendsHeight} />

      {/* Divider - matches Overview Row/Col structure */}
      <Row className="chart-grid"><Col xs={12} style={{padding: '0 5px'}}><hr style={{padding: '0'}} className="col-xs-12"/></Col></Row>

      {/* Facet charts - 2 per row */}
      <Row className="chart-grid">
        {Array.from({ length: chartCount }).map((_, i) => (
          <Col xs={12} sm={12} md={6} lg={6} key={i}>
            <ChartSkeleton height={300} />
          </Col>
        ))}
      </Row>
    </div>
  )
}

// Wrapper component that shows skeleton or children based on loading state
interface WithSkeletonProps {
  loading: boolean
  skeleton: React.ReactNode
  children: React.ReactNode
}

export const WithSkeleton: React.FC<WithSkeletonProps> = ({ loading, skeleton, children }) => {
  if (loading) {
    return <>{skeleton}</>
  }
  return <>{children}</>
}

/**
 * SkeletonOverlay - A dynamic skeleton that overlays the actual content
 * 
 * This approach solves the "size mismatch" problem by:
 * 1. Always rendering the actual children (to preserve exact dimensions)
 * 2. Overlaying a semi-transparent animated layer when loading
 * 3. Optionally hiding the text content underneath for a cleaner look
 * 
 * Benefits:
 * - No layout shift - the actual content defines the size
 * - Works with any component without manual size matching
 * - Highly reusable across the entire application
 * 
 * Usage:
 *   <SkeletonOverlay loading={isLoading}>
 *     <MetricBoxes summary={summary} />
 *   </SkeletonOverlay>
 */
interface SkeletonOverlayProps {
  /** Whether the content is loading */
  loading: boolean
  /** The content to render (always rendered for layout) */
  children: React.ReactNode
  /** 
   * How to handle the content when loading:
   * - 'dim': Shows content with grey overlay (default)
   * - 'hide': Hides content completely but maintains layout
   * - 'blur': Blurs the content under the overlay
   */
  mode?: 'dim' | 'hide' | 'blur'
  /** 
   * Overlay style variant:
   * - 'pulse': Pulsing opacity animation (default)
   * - 'shimmer': Shimmer effect moving across
   * - 'solid': No animation, just a solid overlay
   */
  variant?: 'pulse' | 'shimmer' | 'solid'
  /** Overlay opacity (0-1). Default: 0.7 for dim, 0.9 for hide */
  overlayOpacity?: number
  /** Overlay background color. Default: #e2e8f0 */
  overlayColor?: string
  /** Border radius for the overlay. Default: 4px */
  borderRadius?: string | number
  /** Additional className for the wrapper */
  className?: string
  /** Additional style for the wrapper */
  style?: React.CSSProperties
  /**
   * Delay (ms) before showing the overlay after loading becomes true.
   * Cleared immediately when loading becomes false. Avoids blink on fast refetches.
   */
  delayMs?: number
}

export const SkeletonOverlay: React.FC<SkeletonOverlayProps> = ({
  loading,
  children,
  mode = 'dim',
  variant = 'pulse',
  overlayOpacity,
  overlayColor = '#e2e8f0',
  borderRadius = '4px',
  className = '',
  style = {},
  delayMs = 0,
}) => {
  const [showOverlay, setShowOverlay] = React.useState(delayMs > 0 ? false : loading)

  React.useEffect(() => {
    if (!loading) {
      setShowOverlay(false)
      return
    }
    if (!delayMs) {
      setShowOverlay(true)
      return
    }
    const timer = window.setTimeout(() => setShowOverlay(true), delayMs)
    return () => window.clearTimeout(timer)
  }, [loading, delayMs])

  // Determine default opacity based on mode
  const defaultOpacity = mode === 'hide' ? 0.95 : mode === 'blur' ? 0.5 : 0.7
  const opacity = overlayOpacity ?? defaultOpacity

  // Get animation style based on variant
  const getAnimationStyle = (): React.CSSProperties => {
    switch (variant) {
      case 'shimmer':
        return {
          background: `linear-gradient(90deg, ${overlayColor} 25%, #f1f5f9 50%, ${overlayColor} 75%)`,
          backgroundSize: '200% 100%',
          animation: 'skeleton-shimmer 1.5s ease-in-out infinite',
        }
      case 'solid':
        return {
          backgroundColor: overlayColor,
        }
      case 'pulse':
      default:
        return {
          backgroundColor: overlayColor,
          animation: 'skeleton-pulse 1.5s ease-in-out infinite',
        }
    }
  }

  // Content visibility style when loading (only once delayed overlay is visible)
  const getContentStyle = (): React.CSSProperties => {
    if (!showOverlay) return {}
    
    switch (mode) {
      case 'hide':
        return { visibility: 'hidden' as const }
      case 'blur':
        return { filter: 'blur(4px)' }
      case 'dim':
      default:
        return { opacity: 0.3 }
    }
  }

  return (
    <div
      className={`skeleton-overlay-wrapper ${className}`}
      style={{
        position: 'relative',
        ...style,
      }}
    >
      {/* Content - always rendered to maintain layout */}
      <div
        className="skeleton-overlay-content"
        style={{
          ...getContentStyle(),
          transition: 'opacity 0.2s ease, filter 0.2s ease, visibility 0.2s ease',
        }}
      >
        {children}
      </div>
      
      {/* Overlay - only shown when loading (after optional delay) */}
      {showOverlay && (
        <div
          className="skeleton-overlay"
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            opacity,
            borderRadius,
            pointerEvents: 'none',
            zIndex: 1,
            ...getAnimationStyle(),
          }}
        />
      )}
    </div>
  )
}

/**
 * SkeletonText - Replaces text content with skeleton bars while preserving layout
 * 
 * Use this inside components to skeleton-ize specific text elements.
 * The skeleton will match the size of the text it replaces.
 * 
 * Usage:
 *   <h3><SkeletonText loading={isLoading}>{formattedValue}</SkeletonText></h3>
 */
interface SkeletonTextProps {
  loading: boolean
  children: React.ReactNode
  /** Width of skeleton when loading. Default: auto (matches content width) */
  width?: string | number
  /** Height of skeleton when loading. Default: 1em */
  height?: string | number
  /** Whether to show a placeholder skeleton or just hide content. Default: true */
  showPlaceholder?: boolean
  className?: string
  style?: React.CSSProperties
}

export const SkeletonText: React.FC<SkeletonTextProps> = ({
  loading,
  children,
  width = '80%',
  height = '0.75em',
  showPlaceholder = true,
  className = '',
  style = {},
}) => {
  if (!loading) {
    return <>{children}</>
  }

  if (!showPlaceholder) {
    return <span style={{ visibility: 'hidden' }}>{children}</span>
  }

  // Render children with color transparent to preserve exact spacing
  // (visibility:hidden can sometimes affect layout, transparent color doesn't)
  // Then overlay the skeleton bar on top
  return (
    <span
      className={`skeleton-text-wrapper ${className}`}
      style={{
        position: 'relative',
        ...style,
      }}
    >
      {/* Transparent children to preserve exact line-height, margins, spacing */}
      <span style={{ color: 'transparent', userSelect: 'none' }}>{children}</span>
      {/* Skeleton bar overlay */}
      <span
        className="skeleton-text"
        style={{
          position: 'absolute',
          top: '50%',
          left: 0,
          transform: 'translateY(-50%)',
          width,
          height,
          backgroundColor: '#e2e8f0',
          borderRadius: '4px',
          animation: 'skeleton-pulse 1.5s ease-in-out infinite',
        }}
      />
    </span>
  )
}

/**
 * Full page skeleton loading UI that mimics the Dashboard layout
 * Used in Root.tsx during initial auth loading.
 */
export const FullPageSkeleton: React.FC = () => (
  <div className="full-page-skeleton" style={{
    minHeight: 'calc(100vh - 52px)',
    padding: '20px',
    background: '#f8f9fa'
  }}>
    {/* Header skeleton */}
    <div style={{
      height: '52px',
      background: '#fff',
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      borderBottom: '1px solid #e9ecef',
      display: 'flex',
      alignItems: 'center',
      padding: '0 20px',
      zIndex: 1000
    }}>
      <Skeleton width="120px" height="30px" />
      <div style={{ display: 'flex', gap: '20px', marginLeft: '40px' }}>
        {[1, 2, 3, 4, 5].map(i => (
          <Skeleton key={i} width="80px" height="20px" />
        ))}
      </div>
    </div>

    {/* Main content skeleton */}
    <div style={{ marginTop: '72px', maxWidth: '1200px', margin: '72px auto 0 auto' }}>
      <DashboardOverviewSkeleton metricCount={6} chartCount={4} />
    </div>
  </div>
)

export default Skeleton
